import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaCalendarAlt, 
  FaClock, 
  FaUser, 
  FaMapMarkerAlt, 
  FaVideo, 
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaEye,
  FaEdit,
  FaTrash,
  FaFilter,
  FaSearch
} from 'react-icons/fa';

interface Session {
  _id: string;
  teacherId: {
    basicInfo: {
      fullName: string;
      country: string;
      profilePicture?: string;
    };
  };
  scheduledDate: string;
  duration: number;
  sessionType: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no-show';
  meetingLink?: string;
  notes?: {
    studentNotes?: string;
  };
  payment: {
    amount: number;
    currency: string;
    status: string;
  };
  review?: {
    rating: number;
    comment: string;
  };
}

const SessionManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchSessions();
  }, [user, navigate]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/teachers/my-sessions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      } else {
        setError('Failed to fetch sessions');
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to cancel this session?')) {
      return;
    }

    try {
      const response = await fetch(`/api/teachers/sessions/${sessionId}/cancel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        // Refresh sessions
        fetchSessions();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to cancel session');
      }
    } catch (error) {
      console.error('Error cancelling session:', error);
      alert('Failed to cancel session');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no-show':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <FaClock className="text-yellow-500" />;
      case 'confirmed':
        return <FaCheckCircle className="text-blue-500" />;
      case 'completed':
        return <FaCheckCircle className="text-green-500" />;
      case 'cancelled':
        return <FaTimes className="text-red-500" />;
      case 'no-show':
        return <FaExclamationTriangle className="text-gray-500" />;
      default:
        return <FaClock className="text-gray-500" />;
    }
  };

  const isSessionUpcoming = (session: Session) => {
    const sessionDate = new Date(session.scheduledDate);
    const now = new Date();
    return sessionDate > now && session.status !== 'cancelled';
  };

  const canCancelSession = (session: Session) => {
    const sessionDate = new Date(session.scheduledDate);
    const now = new Date();
    const hoursUntilSession = (sessionDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilSession >= 24 && session.status === 'pending' || session.status === 'confirmed';
  };

  const filteredSessions = sessions.filter(session => {
    const matchesFilter = filter === 'all' || session.status === filter;
    const matchesSearch = searchTerm === '' || 
      session.teacherId.basicInfo.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.sessionType.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">My Sessions</h1>
            <button
              onClick={() => navigate('/teachers')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Book New Session
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters and Search */}
        <div className="bg-white rounded-2xl shadow-sm border p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="flex-1 w-full md:w-auto">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by teacher name or session type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filter */}
            <div className="flex items-center space-x-2">
              <FaFilter className="text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Sessions</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sessions List */}
        {error ? (
          <div className="text-center py-12">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{error}</h3>
            <button
              onClick={fetchSessions}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📅</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No sessions found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filter !== 'all' 
                ? 'Try adjusting your search or filters.'
                : "You haven't booked any sessions yet."
              }
            </p>
            <button
              onClick={() => navigate('/teachers')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Book Your First Session
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredSessions.map((session) => (
              <div
                key={session._id}
                className="bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  {/* Session Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-lg font-bold mr-4">
                          {session.teacherId.basicInfo.profilePicture ? (
                            <img
                              src={session.teacherId.basicInfo.profilePicture}
                              alt={session.teacherId.basicInfo.fullName}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            session.teacherId.basicInfo.fullName.charAt(0)
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {session.teacherId.basicInfo.fullName}
                          </h3>
                          <div className="flex items-center text-sm text-gray-600">
                            <FaMapMarkerAlt className="mr-1" />
                            {session.teacherId.basicInfo.country}
                            <span className="mx-2">•</span>
                            <span className="capitalize">{session.sessionType}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {getStatusIcon(session.status)}
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                          {session.status}
                        </span>
                      </div>
                    </div>

                    {/* Session Details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center">
                        <FaCalendarAlt className="text-blue-500 mr-2" />
                        <div>
                          <p className="text-sm text-gray-600">Date</p>
                          <p className="font-medium">{formatDate(session.scheduledDate)}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <FaClock className="text-blue-500 mr-2" />
                        <div>
                          <p className="text-sm text-gray-600">Time</p>
                          <p className="font-medium">{formatTime(session.scheduledDate)}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <FaClock className="text-blue-500 mr-2" />
                        <div>
                          <p className="text-sm text-gray-600">Duration</p>
                          <p className="font-medium">{session.duration} minutes</p>
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    {session.notes?.studentNotes && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-1">Your Notes:</p>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border">
                          {session.notes.studentNotes}
                        </p>
                      </div>
                    )}

                    {/* Payment Info */}
                    {session.payment.amount > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-600">
                          Payment: <span className="font-medium">${session.payment.amount.toFixed(2)} {session.payment.currency}</span>
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                            session.payment.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {session.payment.status}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col space-y-2 lg:ml-6">
                    {isSessionUpcoming(session) && session.meetingLink && (
                      <a
                        href={session.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <FaVideo className="mr-2" />
                        Join Meeting
                      </a>
                    )}
                    
                    {canCancelSession(session) && (
                      <button
                        onClick={() => handleCancelSession(session._id)}
                        className="flex items-center justify-center px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        <FaTimes className="mr-2" />
                        Cancel Session
                      </button>
                    )}

                    {session.status === 'completed' && !session.review && (
                      <button
                        onClick={() => navigate(`/session-review/${session._id}`)}
                        className="flex items-center justify-center px-4 py-2 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        <FaEye className="mr-2" />
                        Review Session
                      </button>
                    )}

                    {session.review && (
                      <div className="text-center">
                        <div className="flex items-center justify-center text-yellow-400 mb-1">
                          {Array.from({ length: 5 }, (_, i) => (
                            <FaCheckCircle
                              key={i}
                              className={`w-4 h-4 ${
                                i < session.review!.rating ? 'text-yellow-400' : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-xs text-gray-600">Reviewed</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionManagement;
